import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, InputNumber, Switch, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
// INTERFACES
import type {
  OpcUaNamedSubscriptionForm,
  OpcUaSubscriptionConfigForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;

export const BLANK_SUBSCRIPTION_CONFIG: OpcUaSubscriptionConfigForm = {
  publishingIntervalMs: 1000,
  lifetimeCount: 10,
  maxKeepAliveCount: 30,
  maxNotificationsPerPublish: 0,
  priority: 0,
  samplingIntervalMs: 0,
  queueSize: 1,
  publishingEnabled: true,
};

/** Shared field grid for one SubscriptionConfig (used for both the connection default and each named subscription). */
function SubscriptionConfigFields({
  config,
  onChangeConfig,
}: {
  config: OpcUaSubscriptionConfigForm;
  onChangeConfig: (config: OpcUaSubscriptionConfigForm) => void;
}) {
  const onChangeField = <K extends keyof OpcUaSubscriptionConfigForm>(
    field: K,
    value: OpcUaSubscriptionConfigForm[K],
  ) => onChangeConfig({ ...config, [field]: value });

  return (
    <div className="row">
      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">Publishing Interval (ms)</span>
        <InputNumber
          className="generalStyles__width100"
          size="large"
          min={0}
          value={config.publishingIntervalMs}
          onChange={(v) => onChangeField("publishingIntervalMs", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">Lifetime Count</span>
        <InputNumber
          className="generalStyles__width100"
          size="large"
          min={0}
          value={config.lifetimeCount}
          onChange={(v) => onChangeField("lifetimeCount", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">Max Keep-Alive Count</span>
        <InputNumber
          className="generalStyles__width100"
          size="large"
          min={0}
          value={config.maxKeepAliveCount}
          onChange={(v) => onChangeField("maxKeepAliveCount", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">Max Notifications/Publish</span>
        <InputNumber
          className="generalStyles__width100"
          size="large"
          min={0}
          value={config.maxNotificationsPerPublish}
          onChange={(v) => onChangeField("maxNotificationsPerPublish", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">Priority</span>
        <InputNumber
          className="generalStyles__width100"
          size="large"
          min={0}
          max={255}
          value={config.priority}
          onChange={(v) => onChangeField("priority", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">Sampling Interval (ms)</span>
        <InputNumber
          className="generalStyles__width100"
          size="large"
          min={0}
          value={config.samplingIntervalMs}
          onChange={(v) => onChangeField("samplingIntervalMs", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">Queue Size</span>
        <InputNumber
          className="generalStyles__width100"
          size="large"
          min={0}
          value={config.queueSize}
          onChange={(v) => onChangeField("queueSize", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-3 mt-2">
        <div className="generalStyles__flex">
          <span className="generalStyles__inputLabel">Publishing Enabled</span>
          <Switch
            checked={config.publishingEnabled}
            onChange={(checked) => onChangeField("publishingEnabled", checked)}
            style={{ marginLeft: 8 }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Connection-default subscription settings plus named subscriptions
 * (each referenced by name from a monitor's Subscribe mode).
 */
export default function SubscriptionsEditor({
  subscription,
  onChangeSubscription,
  namedSubscriptions,
  onChangeNamedSubscriptions,
}: {
  subscription: OpcUaSubscriptionConfigForm | null;
  onChangeSubscription: (subscription: OpcUaSubscriptionConfigForm | null) => void;
  namedSubscriptions: OpcUaNamedSubscriptionForm[];
  onChangeNamedSubscriptions: (rows: OpcUaNamedSubscriptionForm[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draftName, setDraftName] = useState("");
  const [draftConfig, setDraftConfig] = useState<OpcUaSubscriptionConfigForm>({
    ...BLANK_SUBSCRIPTION_CONFIG,
  });

  const onToggleDefaultEnabled = (enabled: boolean) =>
    onChangeSubscription(enabled ? { ...BLANK_SUBSCRIPTION_CONFIG } : null);

  const canAddNamed = draftName.trim() !== "";
  const onAddNamed = () => {
    if (!canAddNamed) return;
    onChangeNamedSubscriptions([...namedSubscriptions, { name: draftName, config: draftConfig }]);
    setDraftName("");
    setDraftConfig({ ...BLANK_SUBSCRIPTION_CONFIG });
  };
  const onRemoveNamed = (index: number) => {
    onChangeNamedSubscriptions(namedSubscriptions.filter((_, i) => i !== index));
  };
  const onChangeNamedAt = (index: number, updated: OpcUaNamedSubscriptionForm) => {
    const next = [...namedSubscriptions];
    next[index] = updated;
    onChangeNamedSubscriptions(next);
  };

  return (
    <div className="container mt-3">
      <div className="generalStyles__flex" style={{ marginBottom: 16 }}>
        <span className="generalStyles__inputLabel">Configure connection-default subscription</span>
        <Switch
          checked={subscription !== null}
          onChange={onToggleDefaultEnabled}
          style={{ marginLeft: 8 }}
        />
      </div>
      {subscription && (
        <SubscriptionConfigFields config={subscription} onChangeConfig={onChangeSubscription} />
      )}
      <br />
      <Collapse>
        <Panel
          header={t("general.opcUaNamedSubscriptions", { total: namedSubscriptions.length })}
          key="named-subscriptions"
        >
          {namedSubscriptions.map((named, index) => (
            <div
              key={`${named.name}-${index}`}
              className="mb-3"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div className="generalStyles__flex" style={{ justifyContent: "space-between" }}>
                <FormInputText
                  inputValue={named.name}
                  inputLabelName="Name"
                  inputOnChange={(value) => onChangeNamedAt(index, { ...named, name: value })}
                  inputVariableName="name"
                  isRequired
                  disabledInput={false}
                />
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemoveNamed(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
              <SubscriptionConfigFields
                config={named.config}
                onChangeConfig={(config) => onChangeNamedAt(index, { ...named, config })}
              />
            </div>
          ))}
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <FormInputText
                inputValue={draftName}
                inputLabelName="New Subscription Name"
                inputOnChange={setDraftName}
                inputVariableName="draftName"
                isRequired
                disabledInput={false}
              />
            </div>
            <div className="col-12 col-md-3 mt-2">
              <Button
                onClick={onAddNamed}
                disabled={!canAddNamed}
                className={`generalStyles__width100 ${canAddNamed ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
          <SubscriptionConfigFields config={draftConfig} onChangeConfig={setDraftConfig} />
        </Panel>
      </Collapse>
    </div>
  );
}
